import { identity, mapValues, reduce, some } from 'lodash-es';
import type { ComponentType, FC, ReactElement } from 'react';
import { lazy, useCallback, useRef, useState } from 'react';

import type { ComponentWithoutHOCProps } from 'common/types/component';

import type { WithLoadingProps } from './loading';

export function preloadLazyComponent(
    factory: () => Promise<{ default: ComponentType<unknown> | ComponentWithoutHOCProps<unknown> }>
): unknown {
    const Component = lazy(factory) as unknown as Record<string, unknown>;
    Component.preload = factory;

    return Component;
}

export function useLoading(...loadingKeys: string[]): WithLoadingProps {
    const [isLoading, setIsLoading] = useState(true);
    const loading = useRef(
        reduce(
            loadingKeys,
            (obj, key): { [key: string]: boolean } => {
                obj[key] = true;

                return obj;
            },
            {} as Record<string, boolean>
        )
    );

    function setLoading(targetValue: boolean, loadingKey?: string): void {
        if (loadingKey != null) {
            if (loading.current[loadingKey] == null) {
                throw new Error('Unknown loadingKey!');
            }
            loading.current[loadingKey] = targetValue;
        } else {
            loading.current = mapValues(loading.current, (): boolean => targetValue);
        }
        const isLoading = targetValue || some(loading.current, identity);
        setIsLoading(isLoading);
    }

    const setLoadingDone = useCallback((loadingKey?: string): void => {
        setLoading(false, loadingKey);
    }, []);

    const setLoadingStart = useCallback((loadingKey?: string): void => {
        setLoading(true, loadingKey);
    }, []);

    return {
        isLoading,
        setLoadingDone,
        setLoadingStart,
    };
}

export function withLoading(...loadingKeys) {
    return function <T extends WithLoadingProps>(WrappedComponent: FC<T>) {
        return function (props: Omit<T, keyof WithLoadingProps>): ReactElement {
            const { isLoading, setLoadingStart, setLoadingDone } = useLoading(...loadingKeys);

            return (
                <WrappedComponent
                    {...(props as T)}
                    isLoading={isLoading}
                    setLoadingDone={setLoadingDone}
                    setLoadingStart={setLoadingStart}
                />
            );
        };
    };
}
